'use client';

import { useRouter } from 'next/navigation';

interface Service {
  id: string;
  title: string;
  description: string;
  capabilities: string[];
}

const ServicesInteractive = () => {
  const router = useRouter();

  const services: Service[] = [
    {
      id: 'strategy-institutional-design',
      title: 'Strategy & Institutional Design',
      description: 'We help institutions develop sound strategic frameworks and governance structures that align vision with execution reality. Our approach integrates policy intent with organizational capability, ensuring strategies are both ambitious and achievable.',
      capabilities: [
        'Strategic planning and institutional reform frameworks',
        'Governance architecture and decision-making structures',
        'Policy design with implementation feasibility assessment',
        'Organizational design aligned with strategic objectives',
        'Execution frameworks bridging strategy and operations',
        'Stakeholder alignment and institutional coordination'
      ]
    },
    {
      id: 'leadership-capacity-building',
      title: 'Leadership & Capacity Building',
      description: 'We develop executive and organizational capabilities essential for institutional transformation. Our programs combine strategic thinking with practical application, preparing leaders to navigate complexity and drive sustainable change.',
      capabilities: [
        'Executive development for senior decision-makers',
        'Strategic leadership and institutional thinking',
        'AI literacy and responsible technology adoption',
        'Decision-making frameworks for complex environments',
        'Change leadership and stakeholder management',
        'Organizational capability assessment and development'
      ]
    },
    {
      id: 'change-execution-enablement',
      title: 'Change & Execution Enablement',
      description: 'We support institutions in translating strategic intent into operational reality. Our systematic approach ensures initiatives are properly structured, monitored, and adapted to achieve intended outcomes while building internal execution capacity.',
      capabilities: [
        'Program and project management office (PMO) advisory',
        'Performance management system design',
        'Execution monitoring and adaptive management',
        'Change management and institutional transition',
        'Executive coaching for transformation leaders',
        'Capacity embedding and knowledge transfer'
      ]
    }
  ];

  const handleLearnMore = () => {
    router.push('/contact');
  };

  return (
    <>
      {/* How We Help Section */}
      <section className="bg-white py-10 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-[#0f172a] mb-3 sm:mb-4">
              How We Help Institutions
            </h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              Our services combine strategic design, leadership development, and execution support—ensuring institutions can both envision and achieve sustainable transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Service Pillars */}
      <section className="bg-gray-50 py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto space-y-10 sm:space-y-16">
            {services.map((service, index) => (
              <article key={service.id} className="bg-white border-l-4 border-[#14b8a6] p-6 sm:p-8 lg:p-12">
                {/* Service Number */}
                <div className="flex items-center space-x-4 mb-4 sm:mb-6">
                  <span className="text-4xl sm:text-5xl font-bold text-[#14b8a6]/20">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>

                {/* Title */}
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f172a] mb-4 sm:mb-6">
                  {service.title}
                </h3>

                {/* Executive Description */}
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8 max-w-3xl">
                  {service.description}
                </p>

                {/* Capabilities List */}
                <div className="mb-6 sm:mb-8">
                  <h4 className="text-xs sm:text-sm font-semibold text-[#0f172a] uppercase tracking-wider mb-3 sm:mb-4">
                    Core Capabilities
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {service.capabilities.map((capability, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <span className="text-[#14b8a6] mr-2 sm:mr-3 mt-1 flex-shrink-0">→</span>
                        <span className="text-xs sm:text-sm leading-relaxed">{capability}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Integrated Approach Section */}
      <section className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f172a] mb-3 sm:mb-4">
                Integrated Approach to Institutional Success
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto px-2">
                Our services work as a comprehensive system for transformation—combining strategic design, leadership development, and execution support.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12">
              <div className="space-y-5 sm:space-y-6">
                <div>
                  <div className="flex items-center space-x-3 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#0f172a] text-white rounded flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">
                      01
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-[#0f172a]">
                      Senior Advisory-Led
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed pl-10 sm:pl-11">
                    Direct involvement of senior advisors ensures strategic continuity and deep institutional understanding.
                  </p>
                </div>

                <div>
                  <div className="flex items-center space-x-3 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#0f172a] text-white rounded flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">
                      02
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-[#0f172a]">
                      Systematic Methodology
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed pl-10 sm:pl-11">
                    Proven four-phase approach: diagnosis, design, implementation, and sustainable embedding.
                  </p>
                </div>
              </div>

              <div className="space-y-5 sm:space-y-6">
                <div>
                  <div className="flex items-center space-x-3 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#0f172a] text-white rounded flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">
                      03
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-[#0f172a]">
                      Capacity Building Focus
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed pl-10 sm:pl-11">
                    Knowledge transfer and capability development ensure sustainability beyond engagement.
                  </p>
                </div>

                <div>
                  <div className="flex items-center space-x-3 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#0f172a] text-white rounded flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">
                      04
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-[#0f172a]">
                      Long-Term Partnership
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed pl-10 sm:pl-11">
                    Extended engagements allowing proper transformation and capability development.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center pt-6 sm:pt-8 border-t border-gray-200">
              <button
                onClick={handleLearnMore}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-[#0f172a] text-white font-semibold rounded hover:bg-[#1e293b] transition-colors duration-200 text-sm sm:text-base"
              >
                Request Initial Conversation
              </button>
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-12 sm:mt-16 text-center">
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
              Explore our systematic methodology and flagship integrated program
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <button
                onClick={() => router.push('/our-approach')}
                className="w-full sm:w-auto px-5 sm:px-6 py-3 border-2 border-[#0f172a] text-[#0f172a] font-semibold rounded hover:bg-[#0f172a] hover:text-white transition-colors duration-200 text-sm sm:text-base"
              >
                Our Approach
              </button>
              <button
                onClick={() => router.push('/flagship-program')}
                className="w-full sm:w-auto px-5 sm:px-6 py-3 border-2 border-[#0f172a] text-[#0f172a] font-semibold rounded hover:bg-[#0f172a] hover:text-white transition-colors duration-200 text-sm sm:text-base"
              >
                Flagship Program
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesInteractive;